import React, { Dispatch, FC, memo, SetStateAction, useState } from 'react';
import { BemMethod, createBem, PlatformTypes } from '@om-fe/utils';
import Overlay from '@om-fe/core/components/overlay/overlay';
import { bemSlider } from '@om-fe/widgets/dist/components/slider/slider.constants';
import { Button } from '@om-fe/widgets/dist/components/button/button';
import { Checkbox } from '@om-fe/widgets/dist/components/checkbox/checkbox';
import { CheckboxType } from '@om-fe/widgets/dist/components/checkbox/checkbox-constants';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import { RangePickerDropdown } from '@om-fe/widgets/dist/components/range-picker/rangepicker-dropdown';
import { RangePickerValue } from '@om-fe/widgets/dist/components/range-picker/rangepicker';
import { SettledBetFilterType } from '@om-fe/widgets/dist/widgets/betting-history/list/betting-history-list.utils';
import { Slider } from '@om-fe/widgets/dist/components/slider/slider';
import { useI18n } from '@om-fe/widgets/dist/common/i18n/i18n.hooks';
import { BemModifiersMap } from '@om-fe/core/core/types';
import { BetStatus } from '@om-fe/models';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';

const bem: BemMethod = createBem('SettledBetsFilters');
const aocBem: BemMethod = createBem('BetslipExpandAll');
const bemSettledBetsFiltersButton: BemMethod = createBem('SettledBetsFiltersButton');
const config: OMFE2ConfigModel = getConfig();

interface SettledBetsFiltersProps {
    readonly opened?: boolean;
    readonly onClose?: () => void;
    readonly useModal?: boolean;
    readonly tabActive: SettledBetFilterType;
    readonly statusActive: BetStatus | undefined;
    readonly onTabChanged: (tab: SettledBetFilterType) => void;
    readonly onStatusChanged: (status: BetStatus | undefined) => void;
    readonly onOpen?: () => void;
    readonly filterDateRange: RangePickerValue;
    readonly minStartDate: Date;
    readonly maxStartDate: Date;
    readonly handleDateRangeChange: (range: RangePickerValue) => void;
    readonly handleCallBackdropDate?: (range: RangePickerValue) => void;
    readonly allBetExpanded?: boolean;
    readonly handleToggle?: () => void;
    readonly handleDateRangeClear?: () => void;
    readonly setClear?: Dispatch<SetStateAction<boolean>>;
    readonly deviceType?: PlatformTypes;
    readonly disabledAllBetExpandButton?: boolean;
}

interface betResultItem {
    readonly id: BetStatus | undefined,
    readonly name: string,
}

export const SettledBetsFilters: FC<SettledBetsFiltersProps> = memo(function SettledBetsFiltersComponent(
    {
        opened,
        onClose,
        tabActive,
        statusActive,
        onStatusChanged,
        onOpen,
        filterDateRange,
        minStartDate,
        maxStartDate,
        handleDateRangeChange,
        useModal = config.betslip?.disabledSettleBetFiltersModal ? false : true,
        handleCallBackdropDate = () => {},
        handleDateRangeClear,
        allBetExpanded,
        handleToggle,
        deviceType,
        disabledAllBetExpandButton,
    }: SettledBetsFiltersProps,
) {
    const { gettext } = useI18n();
    const dateFormat: string | undefined = config.dateFormat?.FormatFullDate;
    const noControlNavButtonOverlap: boolean = config.layout?.noControlNavButtonOverlap ?? false;
    const [showIconClearDateRange, setShowIconClearDateRange] = useState<Boolean>(false);

    const modifiersBetResult: BemModifiersMap = {
        betResultFilterNav: true,
        ItemToStartUnequalWidth: true,
        Small10: true,
        Tertiary: true,
    };

    const itemsBetResultList: betResultItem[] = [
        {
            id: undefined,
            name: gettext('All'),
        },
        {
            id: BetStatus.WON,
            name: gettext('Won'),
        },
        {
            id: BetStatus.LOST,
            name: gettext('Lost'),
        },
        {
            id: BetStatus.CANCELLED,
            name: gettext('Cancelled'),
        },
        {
            id: BetStatus.CASHED_OUT,
            name: gettext('Cashout'),
        },
    ];

    const itemsBetResult: React.ReactNode[] = [];
    itemsBetResultList.forEach((item: betResultItem): void => {
        itemsBetResult.push(
            <div
                className={(statusActive == item.id) ? bemSlider('Action isSelected') : bemSlider('Action')}
                onClick={() => onStatusChanged(item.id)}
            >
                <div className={bemSlider('ItemText')}>
                    {gettext(item.name)}
                </div>
            </div>
        );
    });

    const handleDateRangeBackDrop = (range: RangePickerValue) => {
        if (useModal) {
            handleCallBackdropDate?.(range);
        } else {
            setShowIconClearDateRange(true);
            onClose?.();
        }
    }

    const onDateRangeClear = () => {
        setShowIconClearDateRange(false);
        handleDateRangeClear?.();
    };

    const filterNode: React.ReactNode = (
        <div className={bem({
                extra: !useModal ? 'SettledBetsFiltersV2' : '',
            })}>
            {useModal &&
                <div className={bem('Header')}>
                    <div className={bem('Title')}>{gettext('Filters')}</div>
                    <Button onClick={onClose} >
                        <GeneralIcon name="close" className={bem('CloseIconWrapper')} />
                    </Button>
                </div>
            }
            <div className={bem({
                element: 'Slider',
                extra: 'OM-Nav',
            })}>
                {useModal &&
                    <div className={bem('Title')}>{gettext('BET RESULT')}</div>
                }
                <Slider
                    sliderItems={itemsBetResult}
                    modifiersFromParent={modifiersBetResult}
                    hasArrows={deviceType !== 'mobile'}
                    alwaysShowArrows={true}
                    noControlNavButtonOverlap={noControlNavButtonOverlap}
                />
            </div>
            {tabActive === SettledBetFilterType.PlacementDate &&
                <div className={bem('Content')}>
                    <div className={bem('Title')}>{gettext('DATE RANGE (MAX 90 DAYS)')}</div>
                    <div className={bem('DatePicker')}>
                        <GeneralIcon name="calendar-clear" className={bem('Icon')} />
                        <RangePickerDropdown
                            predefinedRange={filterDateRange}
                            onRangeChange={handleDateRangeChange}
                            minDate={minStartDate}
                            maxDate={maxStartDate}
                            onBackDrop={handleDateRangeBackDrop}
                            focusEndDate={true}
                            dateFormat={dateFormat}
                            allowSetRange={!useModal ? true : false}
                        />
                        {showIconClearDateRange &&                             
                            <GeneralIcon name="close" className={bem('CloseIconWrapper')} onClick={onDateRangeClear}/>
                        }
                    </div>
                    {(!useModal && !disabledAllBetExpandButton) &&
                        <div className={aocBem()}>
                            <Checkbox
                                inputType={CheckboxType.CHECKBOX_SWITCH}
                                name="checkboxSwitch"
                                label={allBetExpanded ? gettext('Collapse all') : gettext('Expand all')}
                                checked={allBetExpanded}
                                onChange={handleToggle}
                            />
                        </div>
                    }
                </div>
            }
        </div>
    );

    return (
        useModal
            ? <>
                <Button className={bemSettledBetsFiltersButton()} onClick={onOpen}>
                    <GeneralIcon name="preferences" className={bemSettledBetsFiltersButton('Icon')} />
                    <span className={bemSettledBetsFiltersButton('Label')}>{gettext('Filters')}</span>
                </Button>
                <Overlay
                    opened={opened}
                    onClose={onClose}
                    className={bem('Overlay')}
                >
                    {filterNode}
                </Overlay>
            </>
            : filterNode
    )
});
